import CryptoJS from 'crypto-js';
import Cookie from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import { getChatPlatformInfo, postRequest, getRequest, postChatMessages } from '@/services';
import { AI_ASSISTANT_SEED_MESSAGE } from '@/constants';

const CHAT_SESSION_COOKIE_NAME = 'cec_chat.session_id';

const model = {
  namespace: 'chatbotModel',
  state: {
    loading: false,
    postMessagesLoading: false,
    error: '',
    chatPlatformInfo: {},
    chatbotMessages: [],
    chatbotVisible: false,
    chatbotExpanded: false,
    chatbotDetached: false,
    // Chatbot API params:
    chatbotSourceId: '',
    chatbotPubsubToken: '',
    chatbotConversationId: null,
    chatbotFeedback: {},
  },
  subscriptions: {},
  effects: {
    *openDialog(_, { select, put }) {
      try {
        const { chatbotVisible } = yield select(({ chatbotModel }) => ({
          chatbotVisible: chatbotModel.chatbotVisible,
        }));

        yield put.resolve({ type: 'userModel/getUsersProfile' });

        yield put({ type: 'setState', payload: { chatbotVisible: !chatbotVisible } });
      } catch (e) {
        yield put({ type: 'appMode/handleError', payload: e });
      }
    },
    *setupChatwoot(_, { call, select, put }) {
      try {
        const { singlePage, profile } = yield select(({ routerModel, userModel }) => ({
          singlePage: routerModel.singlePage,
          profile: userModel.profile,
        }));

        if (singlePage || !profile.username) return;

        const chatPlatformInfo = yield call(getChatPlatformInfo);

        if (!chatPlatformInfo || !chatPlatformInfo.serviceUrl) return;

        yield put({ type: 'setState', payload: { chatPlatformInfo } });

        yield put.resolve({
          type: 'setState',
          payload: { chatbotSourceId: Cookie.get('cec_cb.source_id') },
        });

        yield put({ type: 'updateChatbotFeedback' });
        yield put({ type: 'setContacts' });
        yield put({ type: 'setConversations' });
      } catch (e) {
        yield put({ type: 'appMode/handleError', payload: e });
      }
    },
    *setContacts(_, { call, select, put }) {
      try {
        const { chatPlatformInfo, chatbotSourceId } = yield select(({ chatbotModel }) => ({
          chatPlatformInfo: chatbotModel.chatPlatformInfo,
          chatbotSourceId: chatbotModel.chatbotSourceId,
        }));

        if (!chatbotSourceId) return;

        const { pubsub_token } = yield call(
          getRequest,
          `${chatPlatformInfo.serviceUrl}/public/api/v1/inboxes/${chatPlatformInfo.inboxIdentifier}/contacts/${chatbotSourceId}`,
        );

        yield put({ type: 'setState', payload: { chatbotPubsubToken: pubsub_token } });
      } catch (e) {
        yield put({ type: 'setState', payload: { error: e.error } });
      }
    },
    *setConversations(_, { call, select, put }) {
      try {
        const { chatPlatformInfo, chatbotSourceId } = yield select(({ chatbotModel }) => ({
          chatPlatformInfo: chatbotModel.chatPlatformInfo,
          chatbotSourceId: chatbotModel.chatbotSourceId,
        }));

        if (!chatbotSourceId) return;

        const data = yield call(
          getRequest,
          `${chatPlatformInfo.serviceUrl}/public/api/v1/inboxes/${chatPlatformInfo.inboxIdentifier}/contacts/${chatbotSourceId}/conversations`,
        );

        const conversation = [...data].sort((a, b) => b.id - a.id)[0];
        if (conversation && conversation.status !== 'resolved') {
          yield put({
            type: 'setState',
            payload: {
              chatbotConversationId: conversation.id,
              chatbotAccountId: conversation.contact.account_id,
            },
          });
        }
      } catch (e) {
        yield put({ type: 'setState', payload: { error: e.error } });
      }
    },
    *postContacts(_, { call, select, put }) {
      try {
        const { chatPlatformInfo, chatbotSourceId, profile } = yield select(
          ({ chatbotModel, userModel }) => ({
            chatPlatformInfo: chatbotModel.chatPlatformInfo,
            chatbotSourceId: chatbotModel.chatbotSourceId,
            profile: userModel.profile,
          }),
        );

        if (chatbotSourceId) return;

        const { source_id, pubsub_token } = yield call(
          postRequest,
          `${chatPlatformInfo.serviceUrl}/public/api/v1/inboxes/${chatPlatformInfo.inboxIdentifier}/contacts`,
          {
            headers: { 'Content-Type': 'application/json; charset=utf-8' },
            body: JSON.stringify({
              identifier: profile.username,
              identifier_hash: CryptoJS.HmacSHA256(
                profile.username,
                chatPlatformInfo.userIdValidation,
              ).toString(),
              email: profile.email,
              name: `${profile.firstName} ${profile.lastName}`,
              company_name: profile.parentAccountName,
            }),
          },
        );

        Cookie.set('cec_cb.source_id', source_id);
        yield put({
          type: 'setState',
          payload: { chatbotSourceId: source_id, chatbotPubsubToken: pubsub_token },
        });
      } catch (e) {
        yield put({ type: 'setState', payload: { error: e.error } });
      }
    },
    *postConversations(_, { call, select, put }) {
      try {
        const { chatPlatformInfo, chatbotConversationId, chatbotSourceId } = yield select(
          ({ chatbotModel }) => ({
            chatPlatformInfo: chatbotModel.chatPlatformInfo,
            chatbotConversationId: chatbotModel.chatbotConversationId,
            chatbotSourceId: chatbotModel.chatbotSourceId,
          }),
        );

        if (chatbotConversationId) return;

        const { id, contact } = yield call(
          postRequest,
          `${chatPlatformInfo.serviceUrl}/public/api/v1/inboxes/${chatPlatformInfo.inboxIdentifier}/contacts/${chatbotSourceId}/conversations`,
        );

        yield put({
          type: 'setState',
          payload: { chatbotConversationId: id, chatbotAccountId: contact.account_id },
        });

        // Set conversation session id
        if (Cookie.get(CHAT_SESSION_COOKIE_NAME) === undefined) {
          const session_id = uuidv4();
          Cookie.set(CHAT_SESSION_COOKIE_NAME, session_id);
        }
      } catch (e) {
        yield put({ type: 'setState', payload: { error: e.error } });
      }
    },
    *getMessages(_, { call, select, put }) {
      try {
        yield put({ type: 'setState', payload: { loading: true, error: '' } });

        const { chatPlatformInfo, chatbotConversationId, chatbotSourceId } = yield select(
          ({ chatbotModel }) => ({
            chatPlatformInfo: chatbotModel.chatPlatformInfo,
            chatbotConversationId: chatbotModel.chatbotConversationId,
            chatbotSourceId: chatbotModel.chatbotSourceId,
          }),
        );

        if (!chatbotSourceId || !chatbotConversationId) {
          yield put({ type: 'setState', payload: { chatbotMessages: [] } });

          return;
        }

        const chatbotMessages = yield call(
          getRequest,
          `${chatPlatformInfo.serviceUrl}/public/api/v1/inboxes/${chatPlatformInfo.inboxIdentifier}/contacts/${chatbotSourceId}/conversations/${chatbotConversationId}/messages`,
        );

        const visibleMessages = chatbotMessages.filter(
          (m, i) => !(i === 0 && m.message_type === 0 && m.content === AI_ASSISTANT_SEED_MESSAGE),
        );

        yield put({ type: 'setState', payload: { chatbotMessages: visibleMessages } });
      } catch (e) {
        yield put({ type: 'setState', payload: { error: e.error } });
      } finally {
        yield put({ type: 'setState', payload: { loading: false } });
      }
    },
    *postMessages({ payload, callback }, { call, select, put }) {
      try {
        yield put({ type: 'setState', payload: { postMessagesLoading: true, error: '' } });

        yield put.resolve({ type: 'postContacts' });
        yield put.resolve({ type: 'postConversations' });

        const { chatbotConversationId, chatbotAccountId } = yield select(({ chatbotModel }) => ({
          chatbotConversationId: chatbotModel.chatbotConversationId,
          chatbotAccountId: chatbotModel.chatbotAccountId,
        }));

        if (payload?.content) {
          yield call(postChatMessages, {
            accountId: chatbotAccountId,
            conversationId: chatbotConversationId,
            ...payload,
          });
        }

        callback && callback();
      } catch (e) {
        yield put({ type: 'setState', payload: { error: e.error } });
      } finally {
        yield put({ type: 'setState', payload: { postMessagesLoading: false } });
      }
    },
    *toggleConversationStatus({ payload }, { call, select, put }) {
      try {
        yield put({ type: 'setState', payload: { postMessagesLoading: true, error: '' } });

        const { chatbotConversationId, chatbotAccountId } = yield select(({ chatbotModel }) => ({
          chatbotConversationId: chatbotModel.chatbotConversationId,
          chatbotAccountId: chatbotModel.chatbotAccountId,
        }));

        if (!chatbotAccountId || !chatbotConversationId) return;

        yield call(postChatMessages, {
          accountId: chatbotAccountId,
          conversationId: chatbotConversationId,
          status: payload,
        });

        if (payload === 'resolved') {
          yield put({
            type: 'setState',
            payload: { chatbotConversationId: null, chatbotMessages: [] },
          });
          yield put({ type: 'updateChatbotFeedback', payload: null });

          // Remove conversation session id
          Cookie.remove(CHAT_SESSION_COOKIE_NAME);
        }
      } catch (e) {
        yield put({ type: 'setState', payload: { error: e.error } });
      } finally {
        yield put({ type: 'setState', payload: { postMessagesLoading: false } });
      }
    },
    *updateChatbotFeedback({ payload }, { put }) {
      if (payload === null) Cookie.remove('cec_cb.feedback');

      let chatbotFeedback = {};
      try {
        chatbotFeedback = JSON.parse(decodeURIComponent(Cookie.get('cec_cb.feedback'))) || {};
      } catch {
        chatbotFeedback = {};
      }
      chatbotFeedback = { ...chatbotFeedback, ...payload };

      Cookie.set('cec_cb.feedback', encodeURIComponent(JSON.stringify(chatbotFeedback)));
      yield put({
        type: 'setState',
        payload: { chatbotFeedback },
      });
    },
  },
};

export default model;
